// @ts-check

/**
 * A piece of an example request as plain text.
 * A required piece is part of a tag the request must carry.
 * @typedef {{ text: string, isRequired: boolean }} Segment
 */

/**
 * @typedef {object} ExampleOptions
 * @property {string[]} requiredTagNames
 * @property {string[]} optionalTagNames
 * @property {boolean} isCallerIdRequired
 * @property {boolean} exposeCallerId
 */

const CALLER_ID_TAG = 'CID';
const CALLER_ID_VALUE = '14061571951';
const JORNAYA_TAG_NAMES = ['EntityCode', 'JleadId'];

/**
 * Splits the tags into required and optional names, each name once per list.
 *
 * Jornaya TCPA adds its own tags:
 * - `Required`: to the required names.
 * - `On`: to the optional names.
 * - Any other value adds none.
 *
 * @param {{ tag: string, required: boolean }[] | undefined} tags
 * @param {string | undefined} jornayaTcpa
 * @returns {{ requiredTagNames: string[], optionalTagNames: string[] }}
 */
export function getTagNames(tags, jornayaTcpa) {
  const allTags = tags ?? [];
  const requiredTagNames = allTags.filter(tag => tag.required).map(tag => tag.tag);
  const optionalTagNames = allTags.filter(tag => !tag.required).map(tag => tag.tag);

  if (jornayaTcpa === 'Required') {
    requiredTagNames.push(...JORNAYA_TAG_NAMES);
  }

  if (jornayaTcpa === 'On') {
    optionalTagNames.push(...JORNAYA_TAG_NAMES);
  }

  return { requiredTagNames: [...new Set(requiredTagNames)], optionalTagNames: [...new Set(optionalTagNames)] };
}

/**
 * Builds the example GET request.
 * The query starts with `&` when `url` already has a query, and with `?` otherwise.
 * @param {string | undefined} url
 * @param {ExampleOptions} options
 * @returns {Segment[]}
 */
export function buildGetSegments(url, options) {
  const { requiredTagNames, optionalTagNames, isCallerIdRequired, exposeCallerId } = options;
  const queryStart = url != null && url.includes('?') ? '&' : '?';
  const head = [
    plain(url + queryStart),
    callerIdTag(isCallerIdRequired),
    plain('=' + CALLER_ID_VALUE + (exposeCallerId ? '&exposeCallerId=yes' : '')),
  ];

  if (!hasTags(options)) {
    return [...head, plain('&Tag1=TagValue1&Tag2=TagValue2')];
  }

  return [
    ...head,
    ...requiredTagNames.flatMap(name => [plain('&'), required(name), plain('=value')]),
    ...optionalTagNames.map(name => plain('&' + name + '=value')),
  ];
}

/**
 * Builds the example JSON body.
 * @param {ExampleOptions} options
 * @returns {Segment[]}
 */
export function buildJsonBodySegments(options) {
  const { requiredTagNames, optionalTagNames, isCallerIdRequired, exposeCallerId } = options;
  const head = [
    plain('{\n\t"'),
    callerIdTag(isCallerIdRequired),
    plain('": "' + CALLER_ID_VALUE + '",' + (exposeCallerId ? '\n\t"ExposeCallerId": "yes",' : '')),
  ];

  if (!hasTags(options)) {
    return [...head, plain('\n\t"Tag1": "TagValue1",\n\t"Tag2": "TagValue2"\n}')];
  }

  const entries = [
    ...requiredTagNames.map(name => [plain('\n\t"'), required(name), plain('": "value"')]),
    ...optionalTagNames.map(name => [plain('\n\t"' + name + '": "value"')]),
  ];

  return [
    ...head,
    ...entries.flatMap((entry, index) => (index < entries.length - 1 ? [...entry, plain(',')] : entry)),
    plain('\n}'),
  ];
}

/**
 * Builds the example XML body.
 * A required tag is marked with its angle brackets, the caller ID tag by its name only.
 * @param {ExampleOptions} options
 * @returns {Segment[]}
 */
export function buildXmlBodySegments(options) {
  const { requiredTagNames, optionalTagNames, isCallerIdRequired, exposeCallerId } = options;
  const head = [
    plain('<bid_request>\n\t<'),
    callerIdTag(isCallerIdRequired),
    plain('>' + CALLER_ID_VALUE + '</'),
    callerIdTag(isCallerIdRequired),
    plain('>' + (exposeCallerId ? '\n\t<ExposeCallerId>yes</ExposeCallerId>' : '')),
  ];

  if (!hasTags(options)) {
    return [...head, plain('\n\t<Tag1>TagValue1</Tag1>\n\t<Tag2>TagValue2</Tag2>\n</bid_request>')];
  }

  return [
    ...head,
    ...requiredTagNames.flatMap(name => [plain('\n\t'), required('<' + name + '>'), plain('value'), required('</' + name + '>')]),
    ...optionalTagNames.map(name => plain('\n\t<' + name + '>value</' + name + '>')),
    plain('\n</bid_request>'),
  ];
}

/**
 * Wraps `text` as one unmarked segment.
 * Empty or missing text gives no segments.
 * @param {string | undefined} text
 * @returns {Segment[]}
 */
export function plainSegments(text) {
  return text ? [plain(text)] : [];
}

/**
 * Joins the texts of the segments, in order.
 * @param {Segment[]} segments
 * @returns {string}
 */
export function joinSegments(segments) {
  return segments.map(segment => segment.text).join('');
}

/** @param {string} text */
function plain(text) {
  return { text, isRequired: false };
}

/** @param {string} text */
function required(text) {
  return { text, isRequired: true };
}

/** @param {boolean} isRequired */
function callerIdTag(isRequired) {
  return { text: CALLER_ID_TAG, isRequired };
}

/** @param {ExampleOptions} options */
function hasTags({ requiredTagNames, optionalTagNames }) {
  return requiredTagNames.length > 0 || optionalTagNames.length > 0;
}
